import { t } from "@lingui/macro";
import {
  type SortOrder,
  SqlOperator,
  UtilizationReportDataSource,
  UtilizationReportGroupBy,
  type UtilizationReportGroupOrderByColumn,
  type UtilizationReportOrderByColumn,
  UtilizationReportWhereColumn,
} from "@src/__generated__/graphql";
import { UtilizationUserReportModalStore } from "@src/components/modules/reports/utilization/stores/utilization-report-modal";
import type { AppStore } from "@src/stores/AppStore";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { toApiDate } from "@src/utils/dates";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import type { DateRange } from "@src/utils/types";
import { saveQuery } from "@src/utils/url";
import { endOfMonth, startOfMonth } from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";

export class UtilizationReportBaseStore {
  readonly groupByOptions = [
    { label: t`Team`, value: UtilizationReportGroupBy.Team },
    { label: t`Position`, value: UtilizationReportGroupBy.WorkType },
    { label: t`Month`, value: UtilizationReportGroupBy.Month },
    { label: t`Week`, value: UtilizationReportGroupBy.Week },
  ];
  readonly dataSourceOptions = [
    {
      label: t`Tracked & Allocated Hours`,
      value: UtilizationReportDataSource.Combined,
    },
    // { label: t`Tracked Hours`, value: UtilizationReportDataSource.TimeTracking },
    // { label: t`Allocated Hours`, value: UtilizationReportDataSource.Planning },
    {
      label: t`Task budgeted hours`,
      value: UtilizationReportDataSource.TaskPositions,
    },
  ];

  @observable.ref
  dateRange: DateRange = {
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  };

  @observable.ref
  dataSource: UtilizationReportDataSource =
    UtilizationReportDataSource.Combined;

  @observable.ref
  groupBy?: UtilizationReportGroupBy = undefined;

  @observable.ref
  searchTerm = "";

  pagination: PaginationState;
  orderBy = new OrderBy<UtilizationReportOrderByColumn>([]);
  orderByGrouped = new OrderBy<UtilizationReportGroupOrderByColumn>([]);
  where = new Filters<UtilizationReportWhereColumn>(
    [
      new Filter({
        column: UtilizationReportWhereColumn.TeamId,
        operator: SqlOperator.In,
        title: t`Team`,
        options: [],
      }),
      new Filter({
        column: UtilizationReportWhereColumn.WorkTypeId,
        operator: SqlOperator.In,
        title: t`Position`,
        options: [],
      }),
      new Filter({
        column: UtilizationReportWhereColumn.UserId,
        operator: SqlOperator.In,
        title: t`People`,
        options: [],
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
      },
      orderBy: this.orderBy,
    },
  );

  userModalStore: UtilizationUserReportModalStore;

  constructor(
    key: string,
    appStore: AppStore,
    onVariablesChange: (
      pagination: PaginationState,
      groupBy: UtilizationReportGroupBy | undefined,
    ) => void,
  ) {
    makeObservable(this);
    this.where.tableKey = key;
    this.userModalStore = new UtilizationUserReportModalStore(appStore);
    this.pagination = new PaginationState(key, {
      onChangePagination: (pagination) => {
        onVariablesChange(pagination, this.groupBy);
        this.updateUrl();
      },
    });
  }

  @computed
  get activeDataSourceOption() {
    return this.dataSourceOptions.find(
      ({ value }) => value === this.dataSource,
    );
  }

  @computed
  get activeGroupByOption() {
    return this.groupByOptions.find(({ value }) => value === this.groupBy);
  }

  @computed
  get activeOrderBy() {
    return this.orderBy.clauses[0];
  }

  @computed
  get activeOrderByGrouped() {
    return this.orderByGrouped.clauses[0];
  }

  @action.bound
  updateUrl() {
    const query = {
      where: this.where.asURLSearchParam.where,
      orderBy: this.orderBy.asURLSearchParam.orderBy,
      groupOrderBy: this.orderByGrouped.asURLSearchParam.orderBy,
      from: toApiDate(this.dateRange.start),
      to: toApiDate(this.dateRange.end),
      dataSource: this.dataSource,
      groupBy: this.groupBy,
      page: this.pagination.asURLSearchParam.page,
      first: this.pagination.asURLSearchParam.first,
    };
    Router.replace({
      query,
    });
    saveQuery(query);
  }

  @action.bound
  handleChangeDateRange(dateRange: DateRange) {
    this.dateRange = dateRange;
    this.pagination.resetPage();
  }

  @action.bound
  handleChangeGroupBy(value: UtilizationReportGroupBy | undefined) {
    this.groupBy = value;
    this.orderBy.setClauses([]);
    this.pagination.resetPage();
  }

  @action.bound
  handleChangeDataSource(value: UtilizationReportDataSource) {
    this.dataSource = value;
    this.orderBy.setClauses([]);
    this.pagination.resetPage();
  }

  @action.bound
  handleChangeOrderBy(clause: {
    column: UtilizationReportOrderByColumn;
    order: SortOrder | undefined;
  }) {
    if (!clause.order) {
      this.orderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }
    this.orderBy.setClauses([{ column: clause.column, order: clause.order }]);
    this.pagination.resetPage();
  }

  @action.bound
  handleChangeOrderByGrouped(clause: {
    column: UtilizationReportGroupOrderByColumn;
    order: SortOrder | undefined;
  }) {
    if (!clause.order) {
      this.orderByGrouped.setClauses([]);
      this.pagination.resetPage();
      return;
    }
    this.orderByGrouped.setClauses([
      { column: clause.column, order: clause.order },
    ]);
    this.pagination.resetPage();
  }
}
